import React, { useEffect, useState, useRef } from "react";
import { HiOutlinePlus } from "react-icons/hi";
import { IoSearchOutline } from "react-icons/io5";
import { TbRefresh } from "react-icons/tb";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import AddInward from "./AddInward";
import ViewBarrelInward from "./ViewBarrelInward";
import Pagination from "../../Component/Pagination";
import API_URL from "../../Config";
import "../Customer/customer.css";
import "../PurchaseIndents/purchaseIndents.css";
import { MdAttachFile } from "react-icons/md";
import { IoCloseOutline } from "react-icons/io5";
import axios from "axios";

const BarrelInwardList = ({ isSidebarExpanded }) => {
  const [data, setData] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showView, setShowView] = useState(false);
  const [selectedReceiptId, setSelectedReceiptId] = useState(null);
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(null);
  const popupRef = useRef(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    msg: "",
    type: "success",
  });
  const fetchTimeout = useRef(null);

  const handleCloseAttachment = () => {
    setIsPopupOpen(null);
  };

  const handleIconClick = (index) => {
    setIsPopupOpen(index === isPopupOpen ? null : index);
  };

  const handleClearFilters = () => {
    setFromDate("");
    setToDate("");
    setSearch("");
    setPage(0);
  };

  const handleRefresh = () => {
    handleClearFilters();
    fetchData(0, itemsPerPage, "", "", "");
  };

  const showSnackbar = (message, type = "success") => {
    setSnackbar({ open: true, msg: message, type });
  };

  const fetchData = async (currentPage, perPage, searchTerm, from, to) => {
    try {
      setLoading(true);
      const authKey = localStorage.getItem("authKey");

      const params = new URLSearchParams({
        page: currentPage,
        per_page: perPage,
        ...(searchTerm && { search: searchTerm }),
        ...(from && { from_date: from }),
        ...(to && { to_date: to }),
      });

      const res = await fetch(
        `${API_URL}/api/barrel/receipts?${params.toString()}`,
        {
          headers: { Authorization: authKey },
        },
      );

      if (!res.ok) {
        throw new Error("Failed to fetch data");
      }

      const responseData = await res.json();
      setData(responseData.receipts || []);
      setTotalItems(responseData.total || 0);
    } catch (error) {
      console.error("Error fetching data:", error);
      showSnackbar("Failed to load data", "error");
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    clearTimeout(fetchTimeout.current);

    fetchTimeout.current = setTimeout(() => {
      if (search.trim() !== "") {
        setIsSearching(true);
      }
      fetchData(page, itemsPerPage, search, fromDate, toDate);
    }, 600);

    return () => clearTimeout(fetchTimeout.current);
  }, [search, fromDate, toDate, page, itemsPerPage]);

  const handlePaginationChange = (currentPage, itemsPerPage) => {
    setPage(currentPage);
    setItemsPerPage(itemsPerPage);
    localStorage.setItem("currentPage", currentPage);
    localStorage.setItem("itemsPerPage", itemsPerPage);
  };

  const handleViewClick = (receiptId) => {
    setSelectedReceiptId(receiptId);
    setShowView(true);
  };

  const handleCloseView = () => {
    setShowView(false);
    setSelectedReceiptId(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatDateIST = (dateString) => {
    if (!dateString) return "N/A";

    const date = new Date(dateString);

    return date.toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const getStatusClass = (status) => {
    const s = (status || "").toLowerCase();

    if (s === "received") return "received";
    if (s === "completed") return "completed";
    if (s === "draft") return "draft";

    return "default";
  };

  const handleDownloadInvoice = async (receiptId, filename) => {
    try {
      const authKey = localStorage.getItem("authKey");

      const res = await axios.get(
        `${API_URL}/api/barrel/receipts/${receiptId}/invoice/download`,
        {
          headers: { Authorization: authKey },
          responseType: "blob",
        },
      );

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename || "invoice");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download error:", error);
      showSnackbar("Failed to download invoice", "error");
    }
  };

  const handleViewInvoice = async (receiptId) => {
    try {
      const authKey = localStorage.getItem("authKey");

      const res = await axios.get(
        `${API_URL}/api/barrel/receipts/${receiptId}/invoice/view`,
        {
          headers: { Authorization: authKey },
          responseType: "blob",
        },
      );

      const contentType =
        res.headers["content-type"] || "application/octet-stream";

      const blob = new Blob([res.data], { type: contentType });
      const fileURL = window.URL.createObjectURL(blob);

      const newWindow = window.open("", "_blank");

      if (contentType.startsWith("image/")) {
        newWindow.document.write(`
        <html>
          <head><title>Invoice Preview</title></head>
          <body style="margin:0;display:flex;justify-content:center;align-items:center;background:#f5f5f5;">
            <img src="${fileURL}" style="max-width:100%;max-height:100vh;" />
          </body>
        </html>
      `);
      } else if (contentType === "application/pdf") {
        newWindow.document.write(`
        <html>
          <head><title>Invoice Preview</title></head>
          <body style="margin:0">
            <iframe src="${fileURL}" style="width:100%;height:100vh;border:none;"></iframe>
          </body>
        </html>
      `);
      } else {
        newWindow.location.href = fileURL; // fallback
      }

      newWindow.document.close();

      setTimeout(() => window.URL.revokeObjectURL(fileURL), 10000);
    } catch (error) {
      console.error("View error:", error);
      showSnackbar("Failed to view invoice", "error");
    }
  };

  return (
    <>
      <div
        className={`data-list-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="data-list-content">
          <div className="data-list-header">
            <div className="data-list-header-content">
              <div className="data-list-heading-content-left-side">
                <h3>Stock Inward</h3>
              </div>

              <div className="data-list-heading-content-right-side">
                <div style={{ position: "relative", marginLeft: "10px" }}>
                  <label
                    style={{
                      position: "absolute",
                      top: "-8px",
                      left: "12px",
                      background: "#fff",
                      padding: "0 6px",
                      fontSize: "10px",
                      fontWeight: "600",
                      color: "#666",
                      textTransform: "uppercase",
                    }}
                  >
                    From Date
                  </label>

                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    style={{
                      padding: "10px 12px",
                      border: "1px solid #ddd",
                      borderRadius: "6px",
                      fontSize: "12px",
                      minWidth: "150px",
                      textTransform: "uppercase",
                    }}
                  />
                </div>

                <div style={{ position: "relative", marginLeft: "10px" }}>
                  <label
                    style={{
                      position: "absolute",
                      top: "-8px",
                      left: "12px",
                      background: "#fff",
                      padding: "0 6px",
                      fontSize: "10px",
                      fontWeight: "600",
                      color: "#666",
                      textTransform: "uppercase",
                    }}
                  >
                    To Date
                  </label>

                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    style={{
                      padding: "10px 12px",
                      border: "1px solid #ddd",
                      borderRadius: "6px",
                      fontSize: "12px",
                      minWidth: "150px",
                      textTransform: "uppercase",
                    }}
                  />
                </div>

                <div
                  className="search-bar-container"
                  style={{ marginLeft: "10px" }}
                >
                  <input
                    type="text"
                    placeholder="Search supplier / invoice"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="search-bar"
                    style={{ marginLeft: "10px" }}
                  />
                  <IoSearchOutline className="search-icon" />
                </div>

                <button className="new-button" onClick={() => setShowAdd(true)}>
                  + New
                </button>

                <button className="refresh-button" onClick={handleRefresh}>
                  <TbRefresh />
                </button>
              </div>
            </div>
          </div>
          {/* <div className="header-separator"></div> */}

          {/* TABLE */}
          <div className="data-list-body">
            <div className="data-list-scroll-body">
              <div className="data-list-table">
                {loading ? (
                  <div className="loader-container">
                    <div className="loader"></div>
                    <div>Loading...</div>
                  </div>
                ) : data.length > 0 ? (
                  <table className="table multilevel-data">
                    <thead>
                      <tr>
                        <th className="center-alignment">SR. NO.</th>
                        <th>RECEIPT NUMBER</th>
                        <th>RECEIPT DATE</th>
                        <th>SUPPLIER</th>
                        <th>STATUS</th>
                        <th>VEHICLE</th>
                        <th>INVOICE NO</th>
                        <th>TOTAL QTY</th>
                        <th>CREATED AT</th>
                        <th>ATTACHMENT</th>
                        <th className="center-alignment">ACTION</th>
                      </tr>
                    </thead>

                    <tbody className="data-list-scroll-bodys">
                      {data.map((row, index) => (
                        <tr key={row.id} className="hoverable-row">
                          <td className="center-alignment">{index + 1}</td>
                          <td>{row.receipt_number}</td>
                          <td>{formatDate(row.receipt_date)}</td>
                          <td>{row.supplier_name}</td>
                          <td>
                            <span
                              className={`status-badge-inward ${getStatusClass(row.receipt_status)}`}
                            >
                              {row.receipt_status}
                            </span>
                          </td>
                          <td>{row.vehicle_number}</td>
                          <td>{row.invoice_number}</td>
                          <td>{row.total_quantity} KL</td>
                          {/* <td>{formatDateIST(row.created_at)}</td> */}
                          <td>
                            {row.created_at
                              ? new Date(row.created_at)
                                  .toUTCString()
                                  .split("GMT")[0]
                                  .trim() + " IST"
                              : "NA"}
                          </td>
                          <td>
                            {row.invoice_file_path ? (
                              <span
                                className="inquiry-file-btns"
                                onClick={() => handleIconClick(index)}
                              >
                                <MdAttachFile className="inquiry-file-icon" />
                              </span>
                            ) : (
                              "NA"
                            )}

                            {isPopupOpen === index && row.invoice_file_path && (
                              <div
                                className="inquiry-attach-popup Whole-popup"
                                ref={popupRef}
                              >
                                <div className="attach-popup-contant">
                                  <div className="attachment-header">
                                    <div className="attachment-header-content">
                                      Invoice Attachment
                                    </div>
                                    <button
                                      className="fiile-close-btn"
                                      onClick={handleCloseAttachment}
                                    >
                                      <IoCloseOutline className="file-close-icon" />
                                    </button>
                                  </div>

                                  <div className="attach-popup-contant-body">
                                    <div className="attach-popup-content-body">
                                      <div className="inquiry-attach-file-container">
                                        <div className="inquiry-attacth-file-model">
                                          <div className="attach-file-show">
                                            <div className="text-attachment">
                                              {row.invoice_file_name ||
                                                "Invoice File"}
                                            </div>
                                            <div className="text-attachment-file">
                                              File Size:{" "}
                                              {row.invoice_file_size
                                                ? `${(row.invoice_file_size / 1024).toFixed(2)} KB`
                                                : "NA"}
                                            </div>
                                          </div>

                                          <div className="file-footer">
                                            <button
                                              className="file-action-btn"
                                              onClick={() =>
                                                handleDownloadInvoice(
                                                  row.id,
                                                  row.invoice_file_name,
                                                )
                                              }
                                            >
                                              Download
                                            </button>

                                            <button
                                              className="file-action-btn"
                                              onClick={() =>
                                                handleViewInvoice(row.id)
                                              }
                                            >
                                              View
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </td>
                          <td className="center-alignment">
                            <button
                              onClick={() => handleViewClick(row.id)}
                              className="button-bg"
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="no-users-container">
                    <div className="no-users-message">No Data Found</div>
                  </div>
                )}
              </div>
              <Pagination
                showPerPage={itemsPerPage}
                total={totalItems}
                onPaginationChange={handlePaginationChange}
              />
            </div>
          </div>
        </div>

        {/* ADD MODAL */}
        {showAdd && (
          <AddInward
            onClose={() => setShowAdd(false)}
            onSuccess={() => {
              setShowAdd(false);
              fetchData(page, itemsPerPage, search, fromDate, toDate);
              showSnackbar("Inward entry added successfully");
            }}
          />
        )}

        {/* VIEW MODAL */}
        {showView && selectedReceiptId && (
          <ViewBarrelInward
            receiptId={selectedReceiptId}
            onClose={handleCloseView}
            showSnackbar={showSnackbar}
            API_URL={API_URL}
          />
        )}
      </div>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ marginTop: "35px", marginLeft: "20px" }}
      >
        <MuiAlert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.type}
        >
          {snackbar.msg}
        </MuiAlert>
      </Snackbar>
    </>
  );
};

export default BarrelInwardList;
